import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { LeftIcon, RightIcon } from "@ui/components/Icon";
import { Image } from "@ui/components/Image";
import { tv } from "@ui/lib/tv";
import { useCarousel } from "@ui/primitives/Carousel";
import type { AspectRatio } from "@ui/types/media";

const carousel = tv({
  slots: {
    root: "relative overflow-hidden",
    viewport: "overflow-hidden md:rounded-3xl",
    container: "flex",
    item: "relative min-w-0 shrink-0 grow-0 overflow-hidden",
    image: "block w-full object-cover",
    navButton:
      "absolute top-1/2 z-10 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-white/80 shadow-chip backdrop-blur-[2px] transition-opacity",
    navIcon: "",
    prevButton: "",
    nextButton: "",
    dots: "absolute inset-x-0 z-10 flex items-center justify-center gap-2 transition-opacity",
    dot: "rounded-full transition-all duration-500 ease-out",
  },
  variants: {
    aspect: {
      square: { item: "aspect-square", image: "h-full" },
      video: { item: "aspect-video", image: "h-full" },
      landscape: { item: "aspect-[5/3]", image: "h-full" },
    },
    mobile: {
      scroll: {
        viewport: "overflow-visible md:overflow-hidden",
        container: "gap-1 md:gap-0",
        // Round the slide container (which clips via `overflow-hidden`) so both the image
        // and the absolutely-positioned blurhash placeholder behind it get rounded corners.
        // Desktop rounds at the viewport instead, so reset to square here on `md+`.
        item: "basis-[85%] rounded-3xl md:basis-full md:rounded-none",
        navButton: "hidden md:flex",
        dots: "hidden md:flex",
      },
      carousel: {
        item: "basis-full",
      },
    },
    size: {
      default: { navButton: "size-10", navIcon: "size-5" },
      compact: { navButton: "size-7", navIcon: "size-3.5" },
    },
    inset: {
      sm: { prevButton: "left-4", nextButton: "right-4", dots: "bottom-3" },
      md: { prevButton: "left-6", nextButton: "right-6", dots: "bottom-4" },
    },
    controls: {
      visible: {},
      hover: {
        navButton: "[@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
      },
    },
  },
  defaultVariants: {
    mobile: "scroll",
    size: "default",
    inset: "md",
    controls: "visible",
  },
});

export interface CarouselProps {
  /** Aspect ratio applied to each slide ("square", "video", or "landscape" = 5:3). Omit for natural image height. */
  aspect?: AspectRatio | "landscape";
  images: ImageType[];
  className?: string;
  /**
   * LCP optimisation for the first slide. Only the one carousel whose first
   * slide is the page's expected Largest Contentful Paint (e.g. a detail-page
   * hero) should set this — card grids must leave it off so off-screen covers
   * stay `loading="lazy"`. Default: `false`.
   */
  priority?: boolean;
  /** Visibility of nav buttons and dots: "visible" (always) or "hover" (show on hover). */
  controls?: "visible" | "hover";
  /** Inset distance for nav buttons and dots. "sm" = 16px, "md" = 24px (default). */
  inset?: "sm" | "md";
  /** Mobile behavior: "scroll" shows a horizontal scroll strip with peeking,
      "carousel" shows a full carousel with nav buttons and dots (default: "scroll") */
  mobile?: "scroll" | "carousel";
  /** Button and icon dimensions: "default" (40px) or "compact" (28px). */
  size?: "default" | "compact";
  /** CSS `sizes` hint forwarded to each `<Image>`. Default: `"100vw"`. */
  sizes?: string;
}

export function Carousel({
  aspect,
  images,
  className,
  controls = "visible",
  inset = "md",
  mobile = "scroll",
  priority = false,
  size = "default",
  sizes = "100vw",
}: CarouselProps) {
  const styles = carousel({ aspect, controls, inset, mobile, size });

  const {
    emblaRef,
    canScrollPrev,
    canScrollNext,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
    handleKeyDown,
    visibleIndicators,
  } = useCarousel({
    align: mobile === "scroll" ? "start" : "center",
    slideCount: images.length,
  });

  return (
    // biome-ignore lint/a11y/useSemanticElements: role="region" is correct ARIA carousel pattern
    <div
      className={styles.root({ className })}
      onKeyDownCapture={handleKeyDown}
      role="region"
      aria-roledescription="carousel"
    >
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {t("components.carousel.slide_announcement", {
          current: selectedIndex + 1,
          total: scrollSnaps.length,
        })}
      </div>

      <div ref={emblaRef} className={styles.viewport()}>
        <div className={styles.container()}>
          {images.map((image, index) => (
            // biome-ignore lint/a11y/useSemanticElements: role="group" is correct ARIA carousel slide pattern
            <div key={image.src} role="group" aria-roledescription="slide" className={styles.item()}>
              <Image image={image} sizes={sizes} priority={priority && index === 0} className={styles.image()} />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      {canScrollPrev && (
        <button
          type="button"
          aria-label={t("components.carousel.previous_slide")}
          className={styles.prevButton({ class: styles.navButton() })}
          onClick={scrollPrev}
        >
          <LeftIcon className={styles.navIcon()} />
        </button>
      )}
      {canScrollNext && (
        <button
          type="button"
          aria-label={t("components.carousel.next_slide")}
          className={styles.nextButton({ class: styles.navButton() })}
          onClick={scrollNext}
        >
          <RightIcon className={styles.navIcon()} />
        </button>
      )}

      {/* Pagination dots */}
      {scrollSnaps.length > 1 && (
        <nav aria-label={t("components.carousel.slide_navigation")} className={styles.dots()}>
          {visibleIndicators.map(({ index, size: dotSize }) => (
            <button
              key={index}
              type="button"
              aria-label={t("components.carousel.go_to_slide", { number: index + 1 })}
              aria-current={selectedIndex === index ? "true" : undefined}
              className={styles.dot({
                class: `${dotSize === "full" ? "size-2" : "size-1"} ${selectedIndex === index ? "bg-white" : "bg-white/50"}`,
              })}
              onClick={() => scrollTo(index)}
            />
          ))}
        </nav>
      )}
    </div>
  );
}
